"use client";

import React, { Activity } from "react";
import { useTabs } from "./TabsContext";
import { cn } from "utils/utils";

interface TabsContentProps {
  children: React.ReactNode;
  value: string | number;
  className?: string;
  /**
   * Opt out of the Activity wrapper for panels whose children can't survive
   * effects unmounting while hidden. react-leaflet's MapContainer is the known
   * case: it creates its map in a ref callback (never re-run on re-show) but
   * destroys it in an effect cleanup (run on every hide), so any hide/show
   * cycle leaves layers attaching to a destroyed map and trips the map's
   * error boundary.
   */
  disableActivity?: boolean;
}

export function TabsContent({
  children,
  value,
  className,
  disableActivity = false,
}: TabsContentProps) {
  const { activeTab, registerTabPanel } = useTabs();
  const isActive = activeTab === value;

  // Register this panel with the context
  React.useEffect(() => {
    registerTabPanel(value);
  }, [value, registerTabPanel]);

  // Keep content mounted but hidden to prevent Suspense re-triggering.
  // Activity additionally unmounts hidden panels' effects and defers their
  // re-renders to idle priority while preserving state and DOM.
  return (
    <div
      role="tabpanel"
      id={`tab-panel-${value}`}
      aria-labelledby={`tab-trigger-${value}`}
      aria-hidden={!isActive}
      hidden={!isActive}
      className={cn("", className, !isActive && "hidden")}
    >
      {disableActivity ? (
        children
      ) : (
        <Activity mode={isActive ? "visible" : "hidden"}>{children}</Activity>
      )}
    </div>
  );
}
